import { useState, useEffect, useRef } from 'react'
import { Check, AlertCircle, Printer, FileSpreadsheet } from 'lucide-react'
import { toast } from 'sonner'
import { cashHistoryService } from '../../../services/cash-history.service'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/app/components/ui/dialog'
import { Button } from '@/app/components/ui/button'
import { Input } from '@/app/components/ui/input'

export interface CashCountModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  denominations: Record<number, number>
  handleDenominationChange: (denom: number, count: number) => void
  countedCash: string
  setCountedCash: (cash: string) => void
  handleCashCount: () => void
  setShowCashCount: (show: boolean) => void
  isSubmitting?: boolean
  notes: string
  setNotes: (notes: string) => void
  currentCashInRegister: number
}

export function CashCountModal({
  open,
  onOpenChange,
  denominations,
  handleDenominationChange,
  countedCash,
  setCountedCash,
  handleCashCount,
  setShowCashCount,
  isSubmitting = false,
  notes,
  setNotes,
  currentCashInRegister,
}: CashCountModalProps) {
  const parsedCountedCash = parseFloat(countedCash || '0')
  const liveDifference = parsedCountedCash - currentCashInRegister
  const hasDifference = liveDifference !== 0
  const [showError, setShowError] = useState(false)

  useEffect(() => {
    if (parsedCountedCash > 0) {
      setShowError(false)
    }
  }, [parsedCountedCash])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Corte de Caja</DialogTitle>
          <DialogDescription className="sr-only">
            Realizar corte de caja
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="rounded-lg border border-primary/20 bg-card p-4">
            <p className="mb-2 text-sm font-medium text-card-foreground">
              Calculadora de Denominaciones:
            </p>
            <p className="text-xs text-primary">
              Ingresa la cantidad de monedas y billetes que tienes de cada
              denominación
            </p>
          </div>

          {/* Tabla de denominaciones */}
          <div className="overflow-hidden rounded-lg border border-border bg-table-bg text-table-foreground">
            <table className="w-full text-sm">
              <thead className="border-b border-border bg-table-header-bg text-table-header-foreground">
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Denominación
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Cantidad
                  </th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-table-header-foreground uppercase">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {[1000, 500, 200, 100, 50, 20, 10, 5, 2, 1].map((denom) => (
                  <tr key={denom} className="transition-colors hover:bg-table-row-hover">
                    <td className="px-4 py-2 font-medium text-table-foreground">
                      ${denom}
                    </td>
                    <td className="px-4 py-2">
                      <Input
                        type="number"
                        min="0"
                        value={denominations[denom] || ''}
                        onChange={(e) => {
                          const val = parseInt(e.target.value);
                          handleDenominationChange(
                            denom,
                            isNaN(val) ? 0 : Math.max(0, val)
                          );
                        }}
                        onFocus={(e) => e.target.select()}
                        className="h-8 w-24 text-center font-medium"
                        placeholder="0"
                      />
                    </td>
                    <td className="px-4 py-2 font-semibold text-table-foreground">
                      ${(denom * (denominations[denom] || 0)).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-card-foreground">
              Efectivo contado (Total):
            </label>
            <Input
              type="number"
              value={countedCash}
              onChange={(e) => setCountedCash(e.target.value)}
              placeholder="0.00"
             className={`bg-card text-card-foreground text-lg font-bold ${
               showError ? 'border-red-500' : ''
            }`}
              readOnly
            />
            {showError && (
              <p className="text-xs text-red-500">El total debe ser mayor a $0 para confirmar el corte.</p>
            )}
          </div>



          <div className="flex gap-2">
            <Button
              onClick={() => {
                if (parsedCountedCash <= 0) {
                  setShowError(true)
                } else {
                  handleCashCount()
                }
              }}
              disabled={isSubmitting}
              className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {isSubmitting ? 'Confirmando...' : 'Confirmar Corte'}
            </Button>
            <Button
              variant="outline"
              onClick={() => {
                setShowCashCount(false)
                setCountedCash('')
              }}
              disabled={isSubmitting}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export interface CashCountResultModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  cashCountStatus: 'success' | 'shortage' | 'surplus' | null
  cashierNumber: number
  cashierName: string
  currentCashInRegister: number
  countedCash: string
  denominations: Record<number, number>
  cashCountDifference: number
  sessionData?: any
  sucursalName?: string
  closeCashCountResult: () => void
}

export function CashCountResultModal({
  open,
  onOpenChange,
  cashCountStatus,
  cashierNumber,
  cashierName,
  currentCashInRegister,
  countedCash,
  denominations,
  cashCountDifference,
  sessionData,
  sucursalName,
  closeCashCountResult,
}: CashCountResultModalProps) {
  const ticketRef = useRef<HTMLDivElement>(null)
  const [ticketCopies, setTicketCopies] = useState(1)
  const [detail, setDetail] = useState<any>(null)

  useEffect(() => {
    if (open && sessionData) {
      const sessionId = sessionData.session?.id || sessionData.id
      if (sessionId) {
        cashHistoryService.getSession(sessionId)
          .then(setDetail)
          .catch(console.error)
      }
    } else if (!open) {
      setDetail(null)
    }
  }, [open, sessionData])

  const printTicket = () => {
    const ticketEl = ticketRef.current
    if (!ticketEl) return

    const iframe = document.createElement('iframe')
    iframe.style.position = 'fixed'
    iframe.style.top = '-9999px'
    iframe.style.left = '-9999px'
    iframe.style.width = '80mm'
    iframe.style.height = '0'
    iframe.style.border = 'none'
    document.body.appendChild(iframe)

    const doc = iframe.contentDocument ?? iframe.contentWindow?.document
    if (!doc) {
      document.body.removeChild(iframe)
      return
    }

    doc.open()
    const copiesHTML = Array.from({ length: ticketCopies || 1 })
      .map(() => `<div>${ticketEl.innerHTML}</div>`)
      .join('<div style="page-break-after: always;"></div>')

    doc.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Corte de Caja</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    @page { size: 80mm auto; margin: 4mm 3mm; }
    body { font-family: 'Courier New', Courier, monospace; font-size: 11px; color: #000; width: 74mm; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .text-center  { text-align: center; }
    .font-bold    { font-weight: 700; }
    .text-xs      { font-size: 10px; }
    .text-sm      { font-size: 11px; }
    .text-base    { font-size: 13px; }
    .mb-2         { margin-bottom: 4px; }
    .mb-3         { margin-bottom: 6px; }
    .mb-4         { margin-bottom: 8px; }
    .mt-1         { margin-top: 2px; }
    .mt-2         { margin-top: 4px; }
    .mt-3         { margin-top: 6px; }
    .mt-4         { margin-top: 8px; }
    .mt-6         { margin-top: 12px; }
    .pt-1         { padding-top: 2px; }
    .pt-2         { padding-top: 4px; }
    .pt-3         { padding-top: 6px; }
    .space-y-1 > * + * { margin-top: 2px; }
    .space-y-2 > * + * { margin-top: 4px; }
    .border-t          { border-top: 1px dashed #000; }
    .border-b          { border-bottom: 1px dashed #000; }
    .flex            { display: flex; }
    .justify-between { justify-content: space-between; }
    .text-green-600 { color: #16a34a; }
    .text-red-600 { color: #dc2626; }
    .text-yellow-600 { color: #ca8a04; }
    .rounded-lg.border-2.border-dashed { border: none !important; padding: 0 !important; }
    .bg-slate-50 { background-color: transparent !important; }
  </style>
</head>
<body>
  ${copiesHTML}
</body>
</html>`)
    doc.close()

    iframe.onload = () => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      setTimeout(() => {
        if (document.body.contains(iframe)) {
          document.body.removeChild(iframe)
        }
      }, 1500)
    }
  }

  const exportToExcel = async () => {
    const session = sessionData?.session || sessionData
    if (!session || !session.id) {
      toast.error('No se pudo identificar la sesión de caja')
      return
    }
    try {
      toast.info('Generando archivo Excel...')
      const blob = await cashHistoryService.exportSession(session.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `Corte_Caja_${session.id}.xlsx`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      toast.success('Excel exportado correctamente')
    } catch (err: any) {
      toast.error('Error al exportar el archivo Excel')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[92vh] flex-col gap-0 overflow-hidden p-0 sm:max-w-lg">
        <DialogHeader className="border-b border-slate-200 px-5 py-4 pr-14">
          <div className="flex flex-wrap items-center justify-between gap-3 w-full">
            <div>
              <DialogTitle>Desglose de Corte de Caja</DialogTitle>
              <DialogDescription className="sr-only">
                Resultado del corte de caja
              </DialogDescription>
            </div>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                min="1"
                value={ticketCopies || ''}
                onChange={(e) => {
                  const val = parseInt(e.target.value)
                  setTicketCopies(isNaN(val) ? 0 : val)
                }}
                onBlur={() => {
                  if (!ticketCopies || ticketCopies < 1) setTicketCopies(1)
                }}
                className="h-9 w-16 text-center"
                title="Copias a imprimir"
              />
              <Button variant="outline" size="sm" onClick={exportToExcel}>
                <FileSpreadsheet className="mr-2 h-4 w-4 text-green-600" />
                Excel
              </Button>
              <Button variant="default" size="sm" onClick={printTicket}>
                <Printer className="mr-2 h-4 w-4" />
                Imprimir
              </Button>
            </div>
          </div>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-5 space-y-4">
          {/* Status indicator */}
          <div
            className={`rounded-lg p-4 ${cashCountStatus === 'success'
                ? 'border border-green-200 bg-green-50'
                : cashCountStatus === 'surplus'
                  ? 'border border-yellow-200 bg-yellow-50'
                  : 'border border-red-200 bg-red-50'
              }`}
          >
            <div className="flex items-center gap-3">
              {cashCountStatus === 'success' && (
                <>
                  <Check className="h-8 w-8 text-green-600" />
                  <div>
                    <h3 className="font-semibold text-green-900">
                      ¡Corte Exitoso!
                    </h3>
                    <p className="text-sm text-green-700">
                      El efectivo coincide perfectamente
                    </p>
                  </div>
                </>
              )}
              {cashCountStatus === 'surplus' && (
                <>
                  <AlertCircle className="h-8 w-8 text-yellow-600" />
                  <div>
                    <h3 className="font-semibold text-yellow-900">
                      Sobrante Detectado
                    </h3>
                    <p className="text-sm text-yellow-700">
                      Hay más efectivo del registrado
                    </p>
                  </div>
                </>
              )}
              {cashCountStatus === 'shortage' && (
                <>
                  <AlertCircle className="h-8 w-8 text-red-600" />
                  <div>
                    <h3 className="font-semibold text-red-900">
                      Faltante Detectado
                    </h3>
                    <p className="text-sm text-red-700">
                      Hay menos efectivo del registrado
                    </p>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* Desglose */}
          <div
  ref={ticketRef}
  className="rounded-lg border-2 border-dashed border-slate-300 bg-white p-6 font-mono text-sm text-slate-900"
>
            <div className="mb-4 text-center">
              <h3 className="text-base font-bold">Farmacias Salud Plus</h3>
              <p className="text-xs">TICKET DE CORTE DE CAJA</p>
            </div>

            {(() => {
              const session = sessionData?.session || sessionData
              const formatMoney = (val: string | number | null | undefined) => '$' + parseFloat(String(val || '0')).toFixed(2)

              if (!session) return (
                <div className="mb-3 border-t border-dashed border-slate-300 pt-2">
                  <p className="text-xs">Caja: #{cashierNumber}</p>
                  <p className="text-xs">Cajero: {cashierName}</p>
                  <p className="text-xs">Fecha: {new Date().toLocaleString()}</p>
                </div>
              )

              return (
                <>
                  <div className="mb-3 border-t border-dashed border-slate-300 pt-2">
                    <p className="text-xs">Sucursal: {session.sucursal?.name || sucursalName || '—'}</p>
                    <p className="text-xs">Caja: {session.cash_register?.name || '—'}</p>
                    <p className="text-xs">Cajero: {[session.opened_by?.name, session.opened_by?.last_name].filter(Boolean).join(' ') || cashierName}</p>
                    <p className="text-xs">Fecha: {session.closed_at ? new Date(session.closed_at).toLocaleString() : new Date().toLocaleString()}</p>
                  </div>
                  <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                    <h4 className="mb-2 text-xs font-bold text-slate-900">RESUMEN DE SESIÓN</h4>
                    <div className="flex justify-between text-xs"><span>Fondo inicial:</span><span>{formatMoney(session.opening_cash_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Efectivo):</span><span>{formatMoney(session.cash_sales_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Débito):</span><span>{formatMoney(session.card_sales_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Crédito):</span><span>{formatMoney(session.credit_card_sales_amount)}</span></div>
                    <div className="flex justify-between text-xs"><span>Ventas (Transf):</span><span>{formatMoney(session.transfer_sales_amount)}</span></div>
                  </div>

                  {(detail?.movement_summary || sessionData?.movement_summary) && (
                    <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                      <h4 className="mb-2 text-xs font-bold text-slate-900">MOVIMIENTOS DE CAJA</h4>
                      {detail?.movements?.data?.length > 0 ? (
                        <div className="space-y-1 mb-2">
                          {detail.movements.data.map((m: any) => (
                            <div key={m.id} className="flex justify-between text-xs">
                              <span className="truncate pr-2">{m.concept || 'Movimiento'}</span>
                              <span>
                                {m.direction === 'IN' ? '+' : '−'}{formatMoney(m.amount)}
                              </span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-xs text-slate-500 italic mb-2">Sin movimientos registrados</p>
                      )}
                      <div className="flex justify-between text-xs border-t border-slate-200 pt-1">
                        <span>Total ingresos:</span>
                        <span className="font-semibold">{formatMoney((detail || sessionData).movement_summary.incoming_total)}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span>Total egresos:</span>
                        <span className="font-semibold">{formatMoney((detail || sessionData).movement_summary.outgoing_total)}</span>
                      </div>
                    </div>
                  )}

                  <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
                    <div className="flex justify-between text-sm">
                      <span>Efectivo esperado:</span>
                      <span className="font-semibold">{formatMoney(session.expected_cash_amount || currentCashInRegister)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span>Efectivo contado:</span>
                      <span className="font-semibold">{formatMoney(session.counted_cash_amount || countedCash)}</span>
                    </div>
                  </div>
                </>
              )
            })()}

            {/* Desglose de Denominaciones */}
            <div className="space-y-2 border-t border-dashed border-slate-300 pt-3">
              <h4 className="mb-2 text-xs font-bold text-slate-900">
                DESGLOSE DE EFECTIVO
              </h4>
              <div className="space-y-1 rounded bg-slate-50 p-2 pl-2 text-xs">
                {Object.entries(denominations)
                  .sort(([a], [b]) => parseFloat(b) - parseFloat(a))
                  .filter(([_, qty]) => qty > 0)
                  .map(([denom, qty]) => (
                    <div key={denom} className="flex justify-between">
                      <span>
                        ${denom} x {qty}:
                      </span>
                      <span>
                        ${(parseFloat(denom) * qty).toLocaleString()}
                      </span>
                    </div>
                  ))}
                {Object.values(denominations).every((v) => v === 0) && (
                  <p className="text-xs text-slate-500 italic">
                    Sin desglose de denominaciones
                  </p>
                )}
              </div>
            </div>

            <div className="mt-3 border-t border-dashed border-slate-300 pt-3">
              <div className="flex justify-between text-base font-bold">
                <span>
                  {cashCountStatus === 'success'
                    ? 'CORTE EXITOSO'
                    : cashCountStatus === 'surplus'
                      ? 'SOBRANTE:'
                      : 'FALTANTE:'}
                </span>
                <span>
                  {cashCountStatus === 'success'
                    ? '$0.00'
                    : `$${Math.abs(cashCountDifference).toFixed(2)}`}
                </span>
              </div>
            </div>

            <div className="mt-4 border-t border-dashed border-slate-300 pt-3 text-center text-xs">
              <p>Firma del cajero</p>
              <div className="mb-2 border-b border-slate-400" style={{ marginTop: '40px' }}></div>
              <p className="mt-4">Firma del supervisor</p>
              <div className="mb-2 border-b border-slate-400" style={{ marginTop: '40px' }}></div>
            </div>
          </div>

          <div className="flex justify-end pt-4">
            <Button onClick={closeCashCountResult} variant="outline" className="w-full sm:w-auto">
              Cerrar
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
